"use client";

import CustomImage from '@/components/image';
import styles from './_safety.module.scss';
import PageAnchor from '@/components/page-anchor';

const REACTIONS_ADULT = [
  { name: 'Fatigue' },
  { name: 'Headache' },
  { name: 'Dizziness' },
  { name: 'Arthralgia' },
  { name: 'Back pain' },
  { name: 'Decreased appetite' },
  { name: 'Myalgia' },
];

const GROUPS_ADULT = [
  {
    id: 'months-0-6',
    hasName: true,
    title: 'Months 0-6',
    sub: { lead: 'Double-blind period\u2002\u2002', emph: 'Forced, fixed GC reduction', note: 'a' },
    columns: [
      { label: 'Placebo', n: '(n=59)', head: 'placebo', val: 'valGray', data: ['15%', '15%', '3%', '0%', '3%', '2%', '3%'] },
      { label: 'CRENESSITY', n: '(n=122)', head: 'eggplant', val: 'valLavender', data: ['25%', '16%', '8%', '7%', '6%', '4%', '4%'] },
    ],
  },
  {
    id: 'months-6-12',
    hasName: false,
    title: 'Months 6-12',
    sub: { lead: 'Open-label period\u2002\u2002Slower, ', emph: 'flexible GC reduction', note: 'a' },
    columns: [
      { label: 'Switched to CRENESSITY', n: '(n=57)', head: 'lavender', val: 'valLilac', data: ['7%', '9%', '5%', '4%', '7%', '0%', '2%'] },
      { label: 'Continued CRENESSITY', n: '(n=117)', head: 'eggplant', val: 'valLilac', data: ['8%', '9%', '3%', '1%', '3%', '1%', '3%'] },
    ],
  },
  {
    id: 'years-1-2',
    hasName: false,
    title: 'Years 1-2',
    sub: { lead: 'Open-label extension', emph: null, note: null },
    columns: [
      { label: 'Combined CRENESSITY', n: '(n=167)', head: 'combined', val: 'valLilac', data: ['5%', '6%', '4%', '2%', '4%', '2%', '1%'] },
    ],
  },
];

const REACTIONS_PEDIATRIC = [
  { name: 'Headache' },
  { name: 'Abdominal pain', note: 'b' },
  { name: 'Fatigue' },
  { name: 'Nasal congestion' },
  { name: 'Epistaxis' },
];

const GROUPS_PEDIATRIC = [
  {
    id: 'months-0-7',
    hasName: true,
    title: 'Months 0-7',
    sub: { lead: 'Double-blind period\u2002\u2002', emph: 'Flexible GC reduction', note: 'a' },
    columns: [
      { label: 'Placebo', n: '(n=33)', head: 'placebo', val: 'valGray', data: ['6%', '0%', '0%', '3%', '0%'] },
      { label: 'CRENESSITY', n: '(n=69)', head: 'eggplant', val: 'valLavender', data: ['25%', '13%', '7%', '7%', '4%'] },
    ],
  },
  {
    id: 'months-7-12',
    hasName: false,
    title: 'Months 7-12',
    sub: { lead: 'Open-label period\u2002\u2002Slower, ', emph: 'flexible GC reduction', note: 'a' },
    columns: [
      { label: 'Switched to CRENESSITY', n: '(n=31)', head: 'lavender', val: 'valLilac', data: ['23%', '6%', '3%', '3%', '0%'] },
      { label: 'Continued CRENESSITY', n: '(n=66)', head: 'eggplant', val: 'valLilac', data: ['14%', '8%', '8%', '3%', '0%'] },
    ],
  },
  {
    id: 'years-1-2',
    hasName: false,
    title: 'Years 1-2',
    sub: { lead: 'Open-label extension', emph: null, note: null },
    columns: [
      { label: 'Combined CRENESSITY', n: '(n=93)', head: 'combined', val: 'valLilac', data: ['4%', '3%', '2%', '3%', '3%'] },
    ],
  },
];

function AdverseReactionsTable({ reactions, groups }) {
  return (
    <div className={styles.groupedTable}>
      <div className={styles.masterHead}>Adverse reaction</div>
      {groups.map((group) => (
        <section
          key={group.id}
          className={`${styles.group}${group.hasName ? ` ${styles.groupHasName}` : ''}`}
          style={{ '--val-cols': group.columns.length }}
        >
          <div className={styles.caption}>
            <span className={styles.captionTitle}>{group.title}</span>
            <span className={styles.captionSub}>
              {group.sub.lead}
              {group.sub.emph && <em>{group.sub.emph}</em>}
              {group.sub.note && <sup>{group.sub.note}</sup>}
            </span>
          </div>
          <div className={styles.headRow}>
            {group.hasName && <div className={styles.nameHead}>Adverse reaction</div>}
            {group.columns.map((col) => (
              <div key={col.label} className={`${styles.colHead} ${styles[col.head]}`}>
                {col.label}
                <span className={styles.colHeadN}>{col.n}</span>
              </div>
            ))}
          </div>
          {reactions.map((reaction, rowIndex) => (
            <div key={reaction.name} className={styles.row}>
              <div className={styles.rowName}>
                {reaction.name}
                {reaction.note && <sup>{reaction.note}</sup>}
              </div>
              <div className={styles.vals}>
                {group.columns.map((col) => (
                  <div key={col.label} className={`${styles.val} ${styles[col.val]}`}>
                    {col.data[rowIndex]}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

export default function HcpClinicalInformationSafety({ index = 0 }) {
  return (
    <section className={styles.container}>
    {index === 0 && <PageAnchor anchorId="trial-1-safety-safety" instant />}
    {index === 1 && <PageAnchor anchorId="trial-2-safety-safety" instant />}
      <div className="container">
        <h1 className="page-heading">
          {index === 0 && <>
            Demonstrated safety and tolerability profile
          </>}
          {index === 1 && <>
            Demonstrated safety and tolerability profile<sup>1</sup>
          </>}
        </h1>
        
        <div className={styles.tableWrapper}>
          <div className={`${styles.tableCaptionWrapper} ${index === 1 ? styles.tableCaptionWrapper_2 : ''}`.trim()}>
            <span className={styles.tableCaption}>
              {index === 0 && <>
                Commonly observed adverse reactions in children over a 2-year period <br className="hidden tablet:inline-block" />(incidence ≥4% with CRENESSITY and more frequent than with placebo during the double-blind period [7 months])<sup>1,3,5</sup>
              </>}
              {index === 1 && <>
                Commonly observed adverse reactions in adults over a 2-year period (incidence ≥4% with CRENESSITY and more frequent than with placebo during the double-blind period [6 months])<sup>1,4,7</sup>
              </>}
            </span>
          </div>

          {index === 0 && (
            <picture>
              <source
                media="(max-width: 767px)"
                srcSet="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Safety-Table_mobile.svg"
                width={327}
                height={1477}
              />
              <CustomImage
                src="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Safety-Table.svg"
                alt=""
                width={1090}
                height={268}
                className={styles.tableImage}
            />
            </picture>
          )}

          {index === 1 && (
            <picture>
              <source
                media="(max-width: 767px)"
                srcSet="/images/page/hcp/clinical-information/Clinical-Information_Adult-Safety-Table_mobile.svg"
                width={327}
                height={1921}
              />
              <CustomImage
                src="/images/page/hcp/clinical-information/Clinical-Information_Adult-Safety-Table.svg"
                alt=""
                width={1090}
                height={268}
                className={styles.tableImage}
            />
            </picture>
          )}

          {index === 0 && (
            <ul className="footnotes">
              <li>
                <sup className="hang">a</sup>
                <span>After the initial 4 weeks of the double-blind period, GC doses were reduced in ≤4 steps to reach a more physiologic range (8-10 mg/m<sup>2</sup>/day) provided androstenedione levels were controlled. For patients who switched to CRENESSITY in the open­-label period, GC doses were reduced approximately monthly from month 8 to month 12 to reach a more physiologic range (8-10 mg/m<sup>2</sup>/day).<sup>6,13</sup></span>
              </li>
              <li>
                <sup className="hang">b</sup>
                <span>Abdominal pain includes abdominal pain, abdominal pain upper, and abdominal pain lower.<sup>1</sup></span>
              </li>
            </ul>
          )}

          <div className={`hcp-callout ${styles.tablecallout} ${index === 1 ? styles.tablecallout_2 : ''}`.trim()}>
            {index === 0 && (
              <span>Rates of adverse reactions declined as treatment continued after the double-blind period. No new safety signals have been observed in long-term, open-label extension studies for both pediatric and adult patients.<sup>5,7,13</sup></span>
            )}
            {index === 1 && (
              <span>Rates of adverse reactions declined as treatment continued after the double-blind period. No new safety signals have been observed in long-term, open-label extension studies for both pediatric and adult patients.<sup>5,7,13</sup></span>
            )}
          </div>

          {index === 1 && (
            <ul className="footnotes">
              <li>
                <sup className="hang">a</sup>
                <span>After the initial 4 weeks of the double-blind period, GC doses were reduced every 2 weeks in ≤4 steps to reach a more physiologic range (15-18 mg/day); from weeks 12 to 20, the GC dose was further adjusted, if needed, to achieve androstenedione control at week 24.  For patients who switched to CRENESSITY in the open-label period, GC doses were reduced on an approximately monthly basis from month 7 to month 10 to reach a more physiologic range (15-18 mg/day).<sup>4</sup></span>
              </li>
            </ul>
          )}
        </div>

        <div className={styles.keyPoints}>
          <CustomImage
            src="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Shield-Icon.svg"
            alt=""
            width={86}
            height={88}
          />
          <ul>
            {index === 0 && <>
              <li>There were no treatment-related serious adverse reactions and few discontinuations in any study period. The serious adverse reactions reported were determined by the investigators to be unlikely related or unrelated to CRENESSITY<sup className="ml-[2px]">3,9,10,26</sup></li>
              <li>Most adverse reactions were mild to moderate<sup>9</sup></li>
              <li>No evident safety concerns related to vital signs, clinical laboratory values, electrocardiography, or neuropsychiatric assessments with CRENESSITY in the <span className="text-nowrap">phase 3 trial<sup>9</sup></span></li>
            </>}
            {index === 1 && <>
              <li>One patient developed worsening rash 2.5 months after discontinuing CRENESSITY, which was considered by the investigators to be possibly related to CRENESSITY. The worsening rash was not counted among the treatment-emergent serious adverse events as it occurred over 28 days after discontinuation of CRENESSITY; however, it is reported here for completeness</li>
              <li>Most adverse reactions, including fatigue, were mild to moderate in intensity and resolved spontaneously<sup>10</sup></li>
              <li>No safety concerns related to vital signs, clinical laboratory tests, electrocardiographic findings, or neuropsychiatric assessments with CRENESSITY in the phase 3 trial<sup>10</sup></li>
            </>}
          </ul>
        </div>
      </div>

      <div className={`container ${index === 0 ? styles.orangeTopDivider : ''}`}>
        {index === 0 && <div className="hcp-callout ">
          <div className="max-w-[850px] m-auto">
            <p>During the double-blind period, there were no incidents of adrenal crisis in children taking placebo or CRENESSITY.<sup>1</sup></p>
            <p>During the open-label period, there was 1 incident of adrenal crisis among the children who switched from placebo to CRENESSITY. During the open-label extension period, there was 1 additional incident of adrenal crisis. Both events resolved and were assessed as not related to CRENESSITY by investigators.<sup>1,5</sup></p>
            <p>Please talk with your patients about the importance of continuing GC dosing to replace cortisol while on CRENESSITY.</p>
          </div>

        </div>}

        {index === 1 && <div className={`hcp-callout ${styles.callout_2}`}>
          <p>During the double-blind period, 1.6% of adults taking CRENESSITY experienced adrenal crisis. No patients on placebo experienced adrenal crisis; however, 1.7% experienced adrenal insufficiency.<sup>1,10</sup></p>
          <p>Please talk with your patients about the importance of continuing GC dosing to replace cortisol while on CRENESSITY.</p>
        </div>}
        {index === 0 && <div className={`${styles.disclaimer} ${styles.disclaimer_2}`}>
          Acute adrenal crisis can occur in patients with underlying adrenal insufficiency, especially in situations associated with increased cortisol need, such as acute illness, serious trauma, or surgical procedures.<sup>1</sup>
        </div>}
        {index === 1 && <div className="container">
          <div className={styles.disclaimer}>
            Acute adrenal crisis can occur in patients with underlying adrenal insufficiency, especially in situations associated with increased cortisol need, such as acute illness, serious trauma, or surgical procedures.<sup>1</sup>
          </div>
        </div>}
      </div>
    </section>
  );
}
